import React, { useEffect, useMemo, useRef, useState } from 'react'
import { line } from 'd3-shape'
import { Delaunay } from 'd3-delaunay'
import { extent, max } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import { axisLeft, axisTop } from 'd3-axis'
import { select, pointer } from 'd3-selection'

import styled from 'styled-components'
import { COLUMNS, HEADERS } from './config'

const Svg = styled.svg`
    user-select: none;

    text {
        font-size: 12px;
        fill: #666;
        font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
        body.dark-theme & {
            fill: #fff;
        }
    }
    text.mark {
        fill: #666;
        body.dark-theme & {
            fill: #fff;
        }
    }
    line.mark {
        stroke: #666;
        stroke-width: 1px;
        body.dark-theme & {
            stroke: #fff;
        }
    }
    g.axis {
        color: #666;
        path {
            color: rgb(204, 214, 235);
            shape-rendering: crispEdges;
        }
        text {
            font-size: 11px;
        }
        body.dark-theme & {
        }
    }
    path.line {
        stroke: rgb(124, 181, 236);
        body.dark-theme & {
            stroke: #f5da68;
        }
    }
    g.surveyTooltip {
        rect {
            fill: #fffefe;
            opacity: 0.8;
            stroke: rgb(124, 181, 236);
            body.dark-theme & {
                fill: #1b273e;
                stroke: #f5da68;
            }
        }
        text {
            color: #333;
        }
        tspan.uom {
            font-size: 10px;
        }
        tspan.label {
            font-weight: 600;
        }
    }
    g.grid {
        line {
            shape-rendering: crispEdges;
            fill: transparent;
            stroke: rgb(230, 230, 230);
            body.dark-theme & {
                opacity: 0.6;
            }
        }
    }
    circle {
        fill: rgb(124, 181, 236);
        body.dark-theme & {
            fill: #f5da68;
        }
    }
`

function Interaction({ width, height, xScale, yScale, state, x, y }: InteractionProps): JSX.Element {
    const ref = useRef()
    const [current, setCurrent] = useState(undefined)

    useEffect(() => {
        if (ref.current) {
            const delaunay = Delaunay.from(
                state.surveys,
                s => s[x],
                s => s[y]
            )
            let timeout = null
            select(ref.current)
                .on('mousemove', e => {
                    const [mx, my] = pointer(e)
                    const i = delaunay.find(xScale.invert(mx), yScale.invert(my))
                    const found = state.surveys[i]

                    if (found) {
                        const distance = Math.hypot(mx - xScale(found[x]), my - yScale(found[y]))
                        // less than 60pixels.
                        if (distance < 60) {
                            if (timeout) {
                                clearImmediate(timeout)
                                timeout = null
                            }
                            setCurrent(found)

                            return
                        }
                    }
                    // hide
                    timeout = setTimeout(() => {
                        setCurrent(undefined)
                    }, 500)
                })
                .on('mouseleave', () => {
                    // hide
                    timeout = setTimeout(() => {
                        setCurrent(undefined)
                    }, 500)
                })

            return () => {
                if (timeout) {
                    clearTimeout(timeout)
                }
            }
        }
    }, [width, height, x, y, state.surveys, xScale, yScale])

    if (state.uom === null) {
        return null
    }

    // tooltip
    const lineHeight = 16
    const tooltipWidth = 220
    const tooltipHeight = lineHeight + lineHeight * COLUMNS.length
    let tx = 0
    let ty = 0

    // define tooltip position.
    if (current) {
        tx = xScale(current[x]) + 10
        if (tx > width - tooltipWidth) {
            tx = xScale(current[x]) - 10 - tooltipWidth
        }

        ty = yScale(current[y]) + 10
        if (ty > height - tooltipHeight) {
            ty = yScale(current[y]) - 10 - tooltipHeight
        }
    }

    return (
        <>
            {current ? (
                <>
                    <line
                        x1={0}
                        x2={width}
                        y1={yScale(current[y])}
                        y2={yScale(current[y])}
                        stroke={'#ddd'}
                        shapeRendering={'crispEdges'}
                        fill={'transparent'}
                    />
                    <line
                        y1={0}
                        y2={height}
                        x1={xScale(current[x])}
                        x2={xScale(current[x])}
                        stroke={'#ddd'}
                        shapeRendering={'crispEdges'}
                        fill={'transparent'}
                    />
                    {state.surveys.map((survey, i) => (
                        <circle
                            key={i}
                            r={survey === current ? 4 : 3}
                            opacity={survey === current ? 1 : 0.5}
                            cx={xScale(survey[x])}
                            cy={yScale(survey[y])}
                        />
                    ))}
                    <g className={'surveyTooltip'} transform={`translate(${tx}, ${ty})`}>
                        <rect width={tooltipWidth} height={tooltipHeight} rx={3}></rect>
                        {COLUMNS.map((col, i) => (
                            <text key={i} transform={`translate(10, ${20 + lineHeight * i})`}>
                                <tspan className={'label'}>{HEADERS[col]}</tspan>: {current[col]}{' '}
                                <tspan className={'uom'}>{state.uom[col]}</tspan>
                            </text>
                        ))}
                    </g>
                </>
            ) : null}
            <rect ref={ref} fill={'transparent'} width={width} height={height}></rect>
        </>
    )
}

export default function SurveyCartesian({ center, width, x, y, height, state }: SurveyCartesianProps): JSX.Element {
    const ref = useRef()

    const ticks = Math.round(Math.min(width, height) / 80)

    const xMargin = 80
    const yMargin = 42

    const calc = useMemo(() => {
        if (center) {
            const xMax = max(state.surveys, s => Math.abs(s[x])) * 1.1
            const yMax = max(state.surveys, s => Math.abs(s[y])) * 1.1

            const mmax = Math.max(xMax, yMax)
            const xScale = scaleLinear()
                .domain([-mmax, mmax])
                .range([0, width - xMargin])
            const yScale = scaleLinear()
                .domain([-mmax, mmax])
                .range([0, height - yMargin])

            return { xScale, yScale }
        } else {
            const xDomain = extent(state.surveys, s => s[x])
            const yDomain = extent(state.surveys, s => s[y])

            // const firstX = state.surveys.find(d => !isNaN(d[x]))
            // const lastX = state.surveys.reverse().find(d => !isNaN(d[x]))
            //
            // // invert xRange last_x it < than first_x
            // const xRange = firstX && lastX && lastX[x] < firstX[x] ? [width - xMargin - 1, 1] : [1, width - xMargin - 1]

            const xRange = [1, width - xMargin - 1]
            const xScale = scaleLinear().domain(xDomain).range(xRange)
            const yScale = scaleLinear()
                .domain(yDomain)
                .range([1, height - yMargin - 1])

            return { xScale, yScale }
        }
    }, [state.surveys, width, height])

    const lineData = useMemo<string>(() => {
        return line()
            .x(s => calc.xScale(s[x]))
            .y(s => calc.yScale(s[y]))
            .defined(s => !isNaN(s[x]) && !isNaN(s[y]))(state.surveys)
    }, [state.surveys, width, height, calc])

    // render x/y axis
    useEffect(() => {
        if (ref.current) {
            select(ref.current).select('g.x-axis').call(axisTop(calc.xScale).ticks(ticks))
            select(ref.current).select('g.y-axis').call(axisLeft(calc.yScale).ticks(ticks))
        }
    }, [ticks, width, height, calc])

    const xTitle = HEADERS[x] + (state.uom[x] ? `(${state.uom[x]})` : '')
    const yTitle = HEADERS[y] + (state.uom[y] ? `(${state.uom[y]})` : '')

    return (
        <Svg width={width} height={height} ref={ref}>
            <g transform={`translate(${xMargin + (width - xMargin) / 2}, ${10})`}>
                <text textAnchor="middle">{xTitle}</text>
            </g>
            <g transform={`translate(${10}, ${yMargin + (height - yMargin) / 2}) rotate(-90)`}>
                <text textAnchor="middle">{yTitle}</text>
            </g>
            <g className="axis x-axis" transform={`translate(${xMargin}, ${yMargin})`} />
            <g className="axis y-axis" transform={`translate(${xMargin}, ${yMargin})`} />
            <g className={'grid'} transform={`translate(${xMargin}, ${yMargin})`}>
                {calc.yScale.ticks(ticks).map((e, i) => {
                    return (
                        <line
                            key={`h${i}`}
                            x1={0}
                            y1={calc.yScale(e)}
                            x2={width - xMargin}
                            y2={calc.yScale(e)}
                            opacity={e === 0 ? 1 : 0.6}
                            strokeWidth={e === 0 ? 2 : 1}
                        />
                    )
                })}
                {calc.xScale.ticks(ticks).map((e, i) => {
                    return (
                        <line
                            key={`v${i}`}
                            x1={calc.xScale(e)}
                            y1={0}
                            x2={calc.xScale(e)}
                            y2={height - yMargin}
                            opacity={e === 0 ? 1 : 0.6}
                            strokeWidth={e === 0 ? 2 : 1}
                        />
                    )
                })}
            </g>
            <g transform={`translate(${xMargin}, ${yMargin})`}>
                <path className={'line'} d={lineData} strokeWidth={2} fill={'transparent'} />
            </g>
            <g transform={`translate(${xMargin}, ${yMargin})`}>
                <Interaction
                    state={state}
                    x={x}
                    y={y}
                    xScale={calc.xScale}
                    yScale={calc.yScale}
                    width={width - xMargin}
                    height={height - yMargin}
                />
            </g>
        </Svg>
    )
}
